<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue'
import { CreditCard, Loader2, ShieldCheck, Tag, Receipt } from 'lucide-vue-next'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js'
import axios from 'axios'

interface BreakdownItem {
  plan_name: string
  plan_type: string
  duration_minutes: number
  base_amount: number
  extra_guests: number
  extra_guest_amount: number
  subtotal: number
}

const props = defineProps<{
  reservationId: string
  stripePublishableKey: string
  total: number
  subtotal: number
  initialBreakdown: BreakdownItem[]
  reservationPath: string
  initialCouponCode?: string
}>()

const loading = ref(false)
const initializing = ref(true)
const error = ref('')
const displayTotal = ref(props.total)
const displaySubtotal = ref(props.subtotal)
const displayDiscount = ref(0)
const displayBreakdown = ref<BreakdownItem[]>(props.initialBreakdown)
const couponCode = ref(props.initialCouponCode || '')
const couponInput = ref(props.initialCouponCode || '')
const couponApplied = ref(!!props.initialCouponCode)
const couponError = ref('')

let stripe: Stripe | null = null
let elements: StripeElements | null = null
let paymentElement: StripePaymentElement | null = null
let clientSecret = ''

const csrfToken = computed(() => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
})

function formatPrice(cents: number): string {
  return cents.toLocaleString('ja-JP')
}

async function initializeStripe() {
  try {
    stripe = await loadStripe(props.stripePublishableKey)
    if (!stripe) {
      error.value = '決済システムの初期化に失敗しました'
      initializing.value = false
      return
    }

    await createPaymentIntent()

    // DOMが表示されてからElementsをマウント
    initializing.value = false
    await nextTick()
    mountPaymentElement()
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data) {
      const msg = e.response.data.error || e.response.data.message
      if (msg) {
        error.value = msg
      } else {
        error.value = '決済の準備に失敗しました'
      }
    } else {
      error.value = '決済の準備に失敗しました'
    }
    initializing.value = false
  }
}

async function createPaymentIntent() {
  if (!stripe) return

  const response = await axios.post('/api/v1/payments', {
    reservation_id: props.reservationId,
    coupon_code: couponCode.value || undefined,
  }, {
    headers: { 'X-CSRF-Token': csrfToken.value },
  })

  // 0円決済の場合はリダイレクト
  if (response.data.free_checkout) {
    const redirectUrl = response.data.redirect_url as string
    window.location.href = `${redirectUrl}?redirect_status=succeeded`
    return
  }

  clientSecret = response.data.client_secret
  if (response.data.breakdown) {
    displayBreakdown.value = response.data.breakdown
  }
  displaySubtotal.value = response.data.subtotal
  displayDiscount.value = response.data.discount
  displayTotal.value = response.data.total
}

function mountPaymentElement() {
  if (!stripe || !clientSecret) return

  // Stripe Elements をマウント（既存があれば破棄）
  if (paymentElement) {
    paymentElement.destroy()
  }

  elements = stripe.elements({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#5a3a26',
        borderRadius: '0px',
        fontFamily: 'inherit',
      },
    },
    locale: 'ja',
  })

  paymentElement = elements.create('payment')
  paymentElement.mount('#payment-element')
}

async function handleSubmit() {
  if (!stripe || !elements) return

  loading.value = true
  error.value = ''

  const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: `${window.location.origin}${props.reservationPath}`,
    },
    redirect: 'if_required',
  })

  if (stripeError) {
    error.value = stripeError.message || '決済に失敗しました'
    loading.value = false
  } else if (paymentIntent && paymentIntent.status === 'succeeded') {
    // 決済成功: サーバーで確定処理を待ってからリダイレクト
    try {
      await axios.post('/api/v1/payments/confirm', {
        payment_intent_id: paymentIntent.id,
      }, {
        headers: { 'X-CSRF-Token': csrfToken.value },
      })
    } catch {
      // confirm APIが失敗してもWebhookで処理されるためリダイレクトは続行
    }
    window.location.href = `${props.reservationPath}?redirect_status=succeeded`
  } else {
    // 予期しない状態（processing等）
    loading.value = false
    error.value = '決済処理中です。しばらくお待ちください。'
  }
}

async function applyCoupon() {
  if (!couponInput.value.trim() || initializing.value) return
  couponCode.value = couponInput.value.trim().toUpperCase()
  couponError.value = ''

  try {
    initializing.value = true
    await createPaymentIntent()
    couponApplied.value = true
    initializing.value = false
    await nextTick()
    mountPaymentElement()
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data?.error) {
      couponError.value = e.response.data.error
    } else {
      couponError.value = 'クーポンの適用に失敗しました'
    }
    couponCode.value = ''
    couponApplied.value = false
  } finally {
    initializing.value = false
  }
}

async function removeCoupon() {
  if (initializing.value) return
  const previousTotal = displayTotal.value
  const previousDiscount = displayDiscount.value
  const previousCouponCode = couponCode.value

  couponCode.value = ''
  couponInput.value = ''
  couponApplied.value = false
  couponError.value = ''
  displayDiscount.value = 0
  displayTotal.value = displaySubtotal.value

  try {
    initializing.value = true
    await createPaymentIntent()
    initializing.value = false
    await nextTick()
    mountPaymentElement()
  } catch {
    // APIエラー時は元の状態に完全に戻す（clientSecretとUI表示の不整合を防止）
    displayTotal.value = previousTotal
    displayDiscount.value = previousDiscount
    couponCode.value = previousCouponCode
    couponApplied.value = true
    error.value = 'クーポン解除に失敗しました。ページを再読み込みしてください。'
  } finally {
    initializing.value = false
  }
}

onMounted(() => {
  initializeStripe()
})
</script>

<template>
  <div>
    <!-- 料金内訳 -->
    <div class="retro-card p-6 mb-6">
      <div class="flex items-center gap-2 mb-4">
        <Receipt
          class="w-5 h-5 text-[#5a3a26]"
          :stroke-width="2.5"
        />
        <h2 class="text-lg font-bold text-[#5a3a26]">
          料金内訳
        </h2>
      </div>
      <div class="space-y-2">
        <div
          v-for="(item, index) in displayBreakdown"
          :key="index"
        >
          <div class="flex justify-between text-sm">
            <span class="text-[#5a3a26] font-bold">{{ item.plan_name }}（{{ item.duration_minutes >= 60 ? Math.floor(item.duration_minutes / 60) + '時間' : '' }}{{ item.duration_minutes % 60 > 0 ? (item.duration_minutes % 60) + '分' : '' }}）</span>
            <span class="text-[#5a3a26]">¥{{ formatPrice(item.base_amount) }}</span>
          </div>
          <div
            v-if="item.extra_guests > 0"
            class="flex justify-between text-sm"
          >
            <span class="text-[#8b5a3b] ml-4">追加{{ item.extra_guests }}名</span>
            <span class="text-[#5a3a26]">¥{{ formatPrice(item.extra_guest_amount) }}</span>
          </div>
        </div>
      </div>

      <!-- 小計（割引がある場合のみ） -->
      <div
        v-if="displayDiscount > 0"
        class="flex justify-between text-sm mt-3"
      >
        <span class="text-[#8b5a3b]">小計</span>
        <span class="text-[#5a3a26]">¥{{ formatPrice(displaySubtotal) }}</span>
      </div>

      <!-- クーポン割引 -->
      <div
        v-if="displayDiscount > 0"
        class="flex justify-between text-sm text-lime-600 font-bold"
      >
        <span>クーポン割引</span>
        <span>-¥{{ formatPrice(displayDiscount) }}</span>
      </div>

      <!-- 合計 -->
      <div class="flex justify-between mt-4 pt-4 border-t-2 border-[#5a3a26]">
        <span class="font-black text-[#5a3a26]">合計</span>
        <span class="text-xl font-black text-[#5a3a26]">¥{{ formatPrice(displayTotal) }}</span>
      </div>
    </div>

    <!-- 決済フォーム -->
    <div class="retro-card p-6">
      <div class="flex items-center gap-2 mb-4">
        <CreditCard
          class="w-5 h-5 text-[#5a3a26]"
          :stroke-width="2.5"
        />
        <h2 class="text-lg font-bold text-[#5a3a26]">
          お支払い情報
        </h2>
      </div>

      <!-- クーポン入力 -->
      <div class="mb-6">
        <label class="block text-sm font-bold text-[#5a3a26] mb-1.5">
          <Tag
            class="inline w-4 h-4 mr-1"
            :stroke-width="2.5"
          />
          クーポンコード（任意）
        </label>
        <div
          v-if="couponApplied"
          class="flex items-center gap-2"
        >
          <span class="inline-flex items-center gap-1 px-3 py-1.5 bg-lime-100 border-2 border-lime-400 text-lime-700 font-bold text-sm">
            <Tag
              class="w-3.5 h-3.5"
              :stroke-width="2.5"
            />
            {{ couponCode }}
          </span>
          <button
            type="button"
            class="text-sm text-red-500 font-bold hover:text-red-700"
            @click="removeCoupon"
          >
            解除
          </button>
        </div>
        <div
          v-else
          class="flex gap-2"
        >
          <input
            v-model="couponInput"
            type="text"
            placeholder="コードを入力"
            class="flex-1 retro-input text-sm"
            @keyup.enter="applyCoupon"
          >
          <button
            type="button"
            class="retro-btn retro-btn-secondary text-xs px-4"
            :disabled="!couponInput.trim()"
            @click="applyCoupon"
          >
            適用
          </button>
        </div>
        <p
          v-if="couponError"
          class="mt-1 text-sm text-red-600 font-bold"
        >
          {{ couponError }}
        </p>
      </div>

      <!-- 初期化中 -->
      <div
        v-if="initializing"
        class="flex items-center justify-center py-8 text-[#8b5a3b]"
      >
        <Loader2 class="w-6 h-6 animate-spin mr-2" />
        <span class="font-bold">決済フォームを準備中...</span>
      </div>

      <template v-else>
        <!-- Stripe Payment Element -->
        <div
          id="payment-element"
          class="mb-6"
        />

        <!-- エラー表示 -->
        <div
          v-if="error"
          class="mb-4 p-3 bg-red-50 border-2 border-red-300 text-red-700 text-sm font-bold"
        >
          {{ error }}
        </div>

        <!-- セキュリティ表示 -->
        <div class="flex items-center gap-2 mb-4 text-xs text-[#8b5a3b]">
          <ShieldCheck
            class="w-4 h-4"
            :stroke-width="2"
          />
          <span>お支払い情報はStripeで安全に処理されます</span>
        </div>

        <!-- 決済ボタン -->
        <button
          type="button"
          class="w-full retro-btn retro-btn-primary text-sm cursor-pointer"
          :disabled="loading || !!error"
          @click="handleSubmit"
        >
          <span
            v-if="loading"
            class="inline-flex items-center"
          >
            <Loader2 class="w-4 h-4 animate-spin mr-2" />
            処理中...
          </span>
          <span v-else>
            ¥{{ formatPrice(displayTotal) }} を支払って予約を確定
          </span>
        </button>
      </template>
    </div>
  </div>
</template>
